import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { nl2br, sanitize } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';

import type { Goal } from 'webapp/models-custom';

interface Args {
	model: Goal;
}

export default class GoalInsightsDscriptionComponent extends Component<Args> {
	@tracked isEditing = false;
	@tracked canExpand = false;
	@tracked isExpanded = false;

	node: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
	}

	get model() {
		return this.args.model;
	}

	get canEdit() {
		return this.model.canEdit;
	}

	get shownDescription() {
		const description = this.model.description || '';
		if (description) {
			return sanitize(nl2br(description), {
				ALLOWED_TAGS: ['br'],
			});
		} else {
			return description;
		}
	}

	@action
	startEditing() {
		this.isEditing = true;
		this.isExpanded = true;
	}

	@action
	expand() {
		this.isExpanded = true;
		this.canExpand = false;
	}

	@action
	retract() {
		this.isExpanded = false;
		this.canExpand = true;
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		this.fillDescriptionToContentEditable();
	}

	@action
	checkIfCanExpand() {
		if (this.isExpanded || this.isEditing) {
			return;
		}

		const container = this.getContentEditableNode();
		if (!container) {
			return;
		}

		const text = container?.querySelector('span') as HTMLElement;

		if (!text) {
			return;
		}

		const textHeight = text.offsetHeight;

		next(() => {
			const containerHeight = container.offsetHeight;
			this.canExpand = textHeight > containerHeight;
		});
	}

	@action
	checkToSaveDescription(e: FocusEvent) {
		const target = e.target as HTMLElement;
		if (target) {
			let newDescription = (target.innerText || '').trim();
			if (newDescription === '<br>') {
				newDescription = '';
			}
			if (this.model.description !== newDescription) {
				this.model.description = newDescription;
				void this.model.saveAndCatch();
			}
			this.isEditing = false;
			this.isExpanded = false;
			this.fillDescriptionToContentEditable();
		}
	}

	@action
	fillDescriptionToContentEditable() {
		if (this.isEditing) {
			return;
		}
		const target = this.getContentEditableNode();
		if (target) {
			const description = this.shownDescription;
			target.innerHTML = description ? `<span>${description}</span>` : '';
		}
		this.checkIfCanExpand();
	}

	private getContentEditableNode() {
		return this.node?.querySelector('p.text') as HTMLElement | null;
	}
	<template>
		<div
			class={{joinClasses
				(array "description" (if this.isEditing "editing") (if this.isExpanded "expanded"))
			}}
			{{didInsert this.setupView}}
			{{didUpdate this.fillDescriptionToContentEditable @model.description}}
		>
			{{! one line because otherwise :empty css for data-placeholder does not work }}
			{{! setting description text here manually because contenteditable adds some <br> if we dont }}
			<p
				class="text"
				contenteditable={{this.canEdit}}
				data-placeholder="Add a description..."
				{{on "focusin" this.startEditing}}
				{{on "focusout" this.checkToSaveDescription}}
			></p>
			{{#if this.canExpand}}
				<a class="action--expand" {{on "click" this.expand}}>Show more</a>
			{{/if}}
			{{#if this.isExpanded}}
				<a class="action--expand" {{on "click" this.retract}}>Show less</a>
			{{/if}}
		</div>
	</template>
}
